/** The interaction prompt.
 *
 * The kernel parks the whole game on `state.pending` and will accept nothing
 * but a `RESPOND_TO_INTERACTION` that names one of `legalResponses`. Without a
 * dialog the first level-up or the first wandering monster hangs the turn loop
 * forever - the human controller is waiting for a command the player has no way
 * to issue. This is that way.
 *
 * The buttons are built from `legalResponses`, never from the prompt kind: the
 * kernel decides what may be answered, and a button the kernel did not offer
 * would be refused the moment it was pressed.
 */

import type {
  ContentRegistry, InteractionResponse, PendingInteraction, SpellDefId,
} from "@game/core";
import { subskillById } from "@game/core";
import { Button, Panel } from "@game/ui";

export interface PromptDialogProps {
  readonly pending: PendingInteraction;
  readonly content: ContentRegistry;
  readonly onRespond: (response: InteractionResponse) => void;
}

function titleOf(prompt: PendingInteraction["prompt"]): string {
  switch (prompt.kind) {
    case "LEVEL_UP": return "Level up";
    case "PICK_SUBSKILL": return "Choose a subskill";
    case "PICK_SPELL": return "Choose a spell";
    case "FIGHT_OR_FLEE": return "Hostile force";
    case "CHOOSE": return "Choose";
    case "CONFIRM": return "Confirm";
    default: return "Message";
  }
}

function bodyOf(prompt: PendingInteraction["prompt"]): string {
  switch (prompt.kind) {
    case "LEVEL_UP":
      return "Your hero has gained a level. Pick the skill to train.";
    case "PICK_SUBSKILL":
      return "A skill has deepened. Pick the branch to follow; it cannot be changed.";
    case "PICK_SPELL":
      return "One of these spells may be learned.";
    case "FIGHT_OR_FLEE":
      return `A hostile force bars the way. Its strength is judged at ${String(prompt.threat)}.`;
    case "CHOOSE":
    case "CONFIRM":
    case "MESSAGE":
      return prompt.text;
    default:
      return "";
  }
}

/** A readable name for the nth option of a prompt that offers a list. */
function optionLabel(
  prompt: PendingInteraction["prompt"], content: ContentRegistry, index: number,
): string {
  if (prompt.kind === "CHOOSE") return prompt.options[index] ?? `Option ${String(index + 1)}`;
  if (prompt.kind === "LEVEL_UP") {
    const id = prompt.choices[index];
    if (id === undefined) return `Option ${String(index + 1)}`;
    const skill = content.skills.get(id as never);
    return skill?.name ?? humanise(id);
  }
  if (prompt.kind === "PICK_SUBSKILL") {
    const id = prompt.choices[index];
    if (id === undefined) return `Option ${String(index + 1)}`;
    // The name lives on the parent skill, which is the only place a subskill
    // is defined: there is no global subskill table to look it up in.
    const skill = content.skills.get(prompt.skill);
    const sub = skill === undefined ? undefined : subskillById(skill, id);
    return sub?.name ?? humanise(id as string);
  }
  if (prompt.kind === "PICK_SPELL") {
    const id: SpellDefId | undefined = prompt.choices[index];
    if (id === undefined) return `Option ${String(index + 1)}`;
    return content.spells.get(id)?.name ?? humanise(id as string);
  }
  return `Option ${String(index + 1)}`;
}

/** The plain-response wording. A fight prompt says "Attack", not "Yes". */
function plainLabel(prompt: PendingInteraction["prompt"], kind: "OK" | "CANCEL"): string {
  if (prompt.kind === "FIGHT_OR_FLEE") return kind === "OK" ? "Attack" : "Retreat";
  if (prompt.kind === "CONFIRM") return kind === "OK" ? "Yes" : "No";
  return kind === "OK" ? "Continue" : "Cancel";
}

function humanise(id: string): string {
  const bare = id.includes(".") ? id.slice(id.lastIndexOf(".") + 1) : id;
  return bare.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function PromptDialog(props: PromptDialogProps): JSX.Element {
  const { pending, content, onRespond } = props;
  const prompt = pending.prompt;

  return (
    <div className="hm-prompt" role="dialog" aria-modal="true" aria-label={titleOf(prompt)}>
      <Panel material="parchment" style={{ maxWidth: 520, minWidth: 320, padding: "18px 20px" }}>
        <div style={{ fontSize: "max(18px, var(--hm-type-floor, 14px))", letterSpacing: "0.05em", marginBottom: 8 }}>
          {titleOf(prompt)}
        </div>
        <div style={{ fontSize: "max(14px, var(--hm-type-floor, 14px))", lineHeight: 1.5, marginBottom: 16 }}>{bodyOf(prompt)}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
          {pending.legalResponses.map((r, i) => (
            <Button
              key={`${r.kind}:${r.kind === "OPTION" ? String(r.index) : ""}:${String(i)}`}
              variant={r.kind === "CANCEL" ? "normal" : "primary"}
              onClick={() => { onRespond(r); }}
            >
              {r.kind === "OPTION" ? optionLabel(prompt, content, r.index) : plainLabel(prompt, r.kind)}
            </Button>
          ))}
        </div>
      </Panel>
    </div>
  );
}
